<template>
    <div>
        <div
            v-if="chartData.rows && chartData.rows.length > 0 && !chartLoading"
            class="ChartContainer"
        >
            <v-chart
                ref="chart"
                :style="{ height: `${windowHeight * 0.39}px` }"
                :option="barChartOptions"
                autoresize
                class="Chart"
            />
        </div>
        <div
            v-else-if="!chartLoading"
            :style="{ height: `${windowHeight * 0.39}px` }"
            class="flex flex-center"
        >
            <WText>
                {{ $t('visualization.customPlotPanels.attributesInfo.noData') }}
            </WText>
        </div>
    </div>
</template>
<script>
import moment from 'moment'
import echartsColors from '@/charts/echartsColors'

export default {
    name: 'BarChart',

    mixins: [],
    props: {
        index: {
            type: [String, Number],
            required: true,
        },
        chartData: {
            type: Object,
            required: true,
        },
        chartCreationProps: {
            type: Object,
            required: true,
        },
        chartLoading: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['get-chart-data'],

    data() {
        return {
            windowHeight: window.innerHeight - 100,
            barChartOptions: {
                title: {
                    text: '',
                    left: 'center',
                },
                tooltip: {
                    trigger: 'axis',
                    axisPointer: {
                        type: 'shadow',
                    },
                },
                grid: {
                    left: 20,
                    right: 40,
                    bottom: 0,
                    top: 10,
                    containLabel: true,
                },
                xAxis: {
                    type: 'value',
                    splitLine: {
                        lineStyle: {
                            type: 'dashed',
                        },
                    },
                    nameLocation: 'middle',
                    nameTextStyle: {
                        align: 'left',
                        verticalAlign: 'top',
                        padding: [15, 0],
                    },
                },
                yAxis: {
                    type: 'category',
                    splitLine: {
                        lineStyle: {
                            type: 'dashed',
                        },
                    },
                    data: [],
                    axisLabel: {
                        formatter: (value) => {
                            const maxLength = 15
                            return value.length > maxLength ? `${value.slice(0, maxLength)}...` : value
                        },
                    },
                },
                series: [
                    {
                        type: 'bar',
                        data: [],
                        itemStyle: {
                            color: echartsColors.bar.fill,
                        },
                    },
                ],
            },
        }
    },

    watch: {
        chartData: {
            handler(newVal, oldVal) {
                if (newVal.rows && newVal.rows.length > 0 && newVal !== oldVal) {
                    this.updateChartOptions(newVal)
                }
            },
            deep: true,
        },
    },

    created() {
        this.$eventBus.on(`updateSortParams-${this.index}`, this.updateSelection)
    },

    methods: {
        updateSelection(selection) {
            const params = {
                sortBy: selection.sortBy ? selection.sortBy : this.chartCreationProps.sortBy,
                sortDirection: selection.sortDirection ? selection.sortDirection : this.chartCreationProps.sortDirection,
            }
            this.$emit('get-chart-data', params)
        },
        updateChartOptions(data) {
            const isDuration = this.chartCreationProps.sortBy !== 'FREQUENCY'
            this.barChartOptions.yAxis.data = data.rows.map((item) => (item.variantId ? item.variantId : item.activity))
            this.barChartOptions.series[0].data = data.rows.map((item) => item.value)

            this.barChartOptions.xAxis = {
                ...this.barChartOptions.xAxis,
                axisLabel: {
                    formatter: (value) => (isDuration ? this.formatDuration(value) : value),
                },
            }
            this.barChartOptions.tooltip = {
                ...this.barChartOptions.tooltip,
                formatter: (params) => {
                    const labelContent = params[0].axisValueLabel
                    const label = this.chartCreationProps.graphGroup === "VARIANTS"
                        ? this.$t('visualization.dashboard.axisLabels.variantId')
                        : this.$t('visualization.dashboard.axisLabels.activityId')
                    const { value } = params[0]
                    return `${label}: ${labelContent}<br>
                      ${isDuration
                        ? `${this.$t('visualization.dashboard.axisLabels.duration')}: ${this.formatDuration(value)}`
                        : `${this.$t('visualization.dashboard.axisLabels.frequency')}: ${value}`
                      }`
                },
            }
        },
        formatDuration(duration) {
            return moment.duration(duration, 'seconds').format('d[d] h[h]:m[m]:s[s]', { largest: 2, trim: false })
        },
    },
}
</script>
<style scoped lang="scss">
.ChartContainer {
  width: 100%;
  height: 100%;
  flex-grow: 1;
  .Chart {
      width: 100%;
      min-width: 100%;
  }
}
</style>
